﻿import { CountUp } from "countup.js";

/**
 * Animates a number, counting from 0 to the target value.
 * Required data attributes on the element containing the number:
 * - data-js-counter: Indicates that the counter logic should be used for this element
 * - data-js-counter-value: The raw value including decimals and decimal separator.
 *    Fallback: innerText. Attribute must be set if innerText contains *anything* else than numbers.
 *    (Note: separator must match data-js-counter-decimal-separator)
 * - data-js-counter-decimals: Number of decimals to include
 * - data-js-counter-decimal-separator: Symbol to separate decimals, usually "," or "."
 * - data-group-separator: Character to separate thousands, usually "," or a space
 *
 * Optional data attributes:
 * - data-js-counter-prefix: Add a string prefix to the animated value
 * - data-js-counter-suffix: Add a string suffix the animated value
 */
export const counter = () => {
  document.querySelectorAll("[data-js-counter]").forEach(element => {
    const component = element as HTMLElement;

    if (!element) {
      return;
    }

    const decimals = parseInt(component.getAttribute("data-js-counter-decimals") ?? "0");
    const decimalSeparator =
      component.getAttribute("data-js-counter-decimal-separator") ?? ".";
    const groupSeparator =
      component.getAttribute("data-js-counter-group-separator") ?? ",";

    const prefix = component.getAttribute("data-js-counter-prefix") ?? undefined;
    const suffix = component.getAttribute("data-js-counter-suffix") ?? undefined;

    const valueAttr = component.getAttribute("data-js-counter-value") ?? "";

    // Enforce English-formatted numbers to ensure parseFloat returns correct value
    const defaultCultureFormattedValue = valueAttr.replace(decimalSeparator, ".");

    const numericValue = parseFloat(defaultCultureFormattedValue);

    if (isNaN(numericValue)) {
      console.error(
        `Counter initialization failed: invalid number set in data-js-counter-value="${valueAttr}"`
      );
      return;
    }

    const counterProcess = new CountUp(component, numericValue, {
      decimal: decimalSeparator,
      decimalPlaces: !isNaN(decimals) ? decimals : 0,
      separator: groupSeparator,
      prefix,
      suffix,
      enableScrollSpy: true,
      scrollSpyOnce: true,
    });

    if (!counterProcess.error?.length) {
      counterProcess.start();
    } else {
      console.error("Failed to start counter", counterProcess.error);
    }
  });
};
